/**
 * Helpers for the two-iframe widget slot layer: resolve "active" widget per frame
 * and build widget src/config (shared with VBufferFrame-style logic).
 */
import type { TFacet } from '@lucimark/shared/models/playlist'
import type { IAssetEntry, IAssetIndex, IQueueFrame } from '@/types'
import { mergeWidgetConfig } from '@lucimark/shared/helpers/mergeWidgetConfig'
import {
  appendFixedDefaultFlags,
  appendMissingFlagParams,
  applyFlagParams,
  ensureTemplatePlaceholders,
  normalizeUrlTemplatePlaceholders,
  stripAllEmptyQueryParams,
  substituteUrlTemplate
} from '@lucimark/shared/helpers/substituteUrlTemplate'
import { SECTION_SUPPRESSION_WIDGET_NO_URL } from '@/constants/sectionSuppressionMessages'

export interface IActiveWidgetPayload {
  urn: string
  src: string
  config: Record<string, unknown>
  width?: number
  height?: number
}

export function isWidgetEntry(entry: IAssetEntry | undefined): entry is IAssetEntry & { isWidget: true } {
  return entry != null && 'isWidget' in entry
}

function isTruthyFlagValue(v: unknown): boolean {
  if (v === undefined || v === null) { return false }
  const s = String(v)
  return s !== '' && s !== 'false' && s !== '0'
}

function getEffectiveFlagKeys(entry: IAssetEntry | undefined, config: Record<string, unknown>): string[] {
  const paramFlags = entry && 'paramKeysFlag' in entry && Array.isArray(entry.paramKeysFlag) ? entry.paramKeysFlag : []
  const keys = new Set<string>(paramFlags)
  for (const key of Object.keys(config)) {
    if (isTruthyFlagValue(config[key])) { keys.add(key) }
  }
  return Array.from(keys)
}

function configForUrl(entry: IAssetEntry | undefined, config: Record<string, unknown>): Record<string, unknown> {
  const fixed = entry && 'fixedDefaultConfig' in entry && entry.fixedDefaultConfig ? entry.fixedDefaultConfig : {}
  const defaults = entry && 'defaultConfig' in entry && entry.defaultConfig ? entry.defaultConfig : {}
  const out = { ...config }
  for (const [k, v] of Object.entries({ ...defaults, ...fixed })) {
    const cur = out[k]
    if (cur === undefined || cur === null || cur === '') { out[k] = v }
  }
  return out
}

export function getWidgetSrc(entry: IAssetEntry | undefined, config?: Record<string, unknown>): string {
  if (!entry) { return '' }
  if ('urlTemplate' in entry && entry.urlTemplate) {
    const cfg = configForUrl(entry, config ?? {})
    const paramFlags = entry && 'paramKeysFlag' in entry && Array.isArray(entry.paramKeysFlag) ? entry.paramKeysFlag : []
    const fixedKeys = entry && 'fixedDefaultConfig' in entry && entry.fixedDefaultConfig ? Object.keys(entry.fixedDefaultConfig) : []
    const defaultKeys = entry && 'defaultConfig' in entry && entry.defaultConfig ? Object.keys(entry.defaultConfig) : []
    const allParamKeys = Array.from(new Set([...paramFlags, ...fixedKeys, ...defaultKeys]))
    const template = ensureTemplatePlaceholders(
      normalizeUrlTemplatePlaceholders(entry.urlTemplate),
      allParamKeys
    )
    let url = substituteUrlTemplate(template, cfg)
    url = stripAllEmptyQueryParams(url)
    if (paramFlags.length > 0) {
      url = applyFlagParams(url, paramFlags)
    }
    const flagKeysForAppend = getEffectiveFlagKeys(entry, cfg)
    if (flagKeysForAppend.length > 0) {
      url = appendMissingFlagParams(url, flagKeysForAppend, cfg)
    }
    const fixed = entry && 'fixedDefaultConfig' in entry ? entry.fixedDefaultConfig : undefined
    url = appendFixedDefaultFlags(url, fixed, paramFlags)
    return url
  }
  return 'src' in entry ? entry.src : ''
}

export function getWidgetWidth(entry: IAssetEntry | undefined): number | undefined {
  return entry && 'isWidget' in entry && entry.isWidget && 'width' in entry ? entry.width : undefined
}

export function getWidgetHeight(entry: IAssetEntry | undefined): number | undefined {
  return entry && 'isWidget' in entry && entry.isWidget && 'height' in entry ? entry.height : undefined
}

export function getEffectiveWidgetConfig(frame: IQueueFrame, facet: TFacet, entry: IAssetEntry | undefined): Record<string, unknown> {
  const assetConfig = entry && 'defaultConfig' in entry && entry.defaultConfig
    ? entry.defaultConfig
    : (entry && 'fixedDefaultConfig' in entry && entry.fixedDefaultConfig ? entry.fixedDefaultConfig : undefined)
  return mergeWidgetConfig({
    assetConfig: assetConfig ?? null,
    playlistConfig: frame.playlistWidgetConfig ?? null,
    facetConfig: facet.widgetConfig ?? null,
    campaignConfig: frame.campaignWidgetConfig ?? null,
    widgetUrn: facet.asset
  })
}

/** When the facet is a widget but the resolved iframe URL is empty (config/template). */
export function getWidgetSectionSuppressionReason(
  frame: IQueueFrame,
  facet: TFacet & { asset: string },
  entry: IAssetEntry | undefined
): string | null {
  if (!isWidgetEntry(entry)) { return null }
  const src = getWidgetSrc(entry, getEffectiveWidgetConfig(frame, facet, entry))
  if (src && src.trim()) { return null }
  return SECTION_SUPPRESSION_WIDGET_NO_URL
}

/**
 * Section key of the first widget facet in frame (by section order), or null if none.
 * Used to position the two-slot layer over the correct section (e.g. footer) instead of the full frame.
 */
export function getActiveWidgetSectionKey(
  frame: IQueueFrame | null | undefined,
  assetIndex: IAssetIndex
): string | null {
  if (!frame?.facets) { return null }
  for (const sectionKey of Object.keys(frame.facets)) {
    const facet = frame.facets[sectionKey] as TFacet & { asset: string } | undefined
    if (!facet?.asset) { continue }
    const entry = assetIndex[facet.asset]
    if (!isWidgetEntry(entry)) { continue }
    const src = getWidgetSrc(entry, getEffectiveWidgetConfig(frame, facet, entry))
    if (!src || !src.trim()) { continue }
    return sectionKey
  }
  return null
}

/**
 * First widget facet in frame (by section order). All widgets (including formerly singleton) are eligible.
 * Returns null if the frame has no widget facet.
 */
export function getActiveWidgetForFrame(
  frame: IQueueFrame | null | undefined,
  assetIndex: IAssetIndex
): IActiveWidgetPayload | null {
  if (!frame?.facets) { return null }
  const sectionKeys = Object.keys(frame.facets)
  for (const sectionKey of sectionKeys) {
    const facet = frame.facets[sectionKey] as TFacet & { asset: string, widgetConfig?: Record<string, unknown> } | undefined
    if (!facet?.asset) { continue }
    const entry = assetIndex[facet.asset]
    if (!isWidgetEntry(entry)) { continue }
    const config = getEffectiveWidgetConfig(frame, facet, entry)
    const src = getWidgetSrc(entry, config)
    if (!src || !src.trim()) { continue }
    return {
      urn: facet.asset,
      src,
      config,
      width: getWidgetWidth(entry),
      height: getWidgetHeight(entry)
    }
  }
  return null
}
